import { useQuery } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { orpc } from '@/api/rpc/client';
import {
  VersionHistorySection,
  type VersionHistoryEntry,
} from './VersionHistorySection';
import { useRestorePricelistVersion } from './usePricelistActions';
import {
  pricelistVersionSizeLabel,
  pricelistVersionSourceLabel,
} from './pricelistVersionLabels';

/** The pricelist's saved table states, newest first, each restorable. */
export function PricelistVersionHistory({
  documentId,
}: {
  documentId: string;
}) {
  const gt = useGT();
  const versionsQuery = useQuery(
    orpc.inventoryManagement.pricelist.listVersions.queryOptions({
      input: { pdfDocumentId: documentId },
    }),
  );
  const restoreMutation = useRestorePricelistVersion();

  const entries: VersionHistoryEntry[] = (
    versionsQuery.data?.versions ?? []
  ).map((version) => ({
    id: version.id,
    versionNumber: version.versionNumber,
    isCurrent: version.isCurrent,
    createdByName: version.createdByName,
    createdAt: version.createdAt,
    detail: `${pricelistVersionSourceLabel(gt, version.source)} · ${pricelistVersionSizeLabel(gt, version)}`,
    // A version whose text outgrew the inline snapshot kept only its counts;
    // the server refuses to restore it, so don't offer to.
    ...(version.contentOmitted
      ? {
          restoreDisabledReason: gt('Too large to restore', {
            context:
              'version history: shown instead of the Restore button when the version kept no content snapshot',
          }),
        }
      : {}),
  }));

  return (
    <VersionHistorySection
      entries={entries}
      isPending={versionsQuery.isPending}
      isRestoring={restoreMutation.isPending}
      confirmMessage={gt(
        "This replaces the pricelist's tables with the contents of that version. The current contents stay in the version history, so this can be undone.",
        {
          context: 'confirmation dialog body for restoring a pricelist version',
        },
      )}
      onRestore={(entry) =>
        restoreMutation.mutateAsync({
          pdfDocumentId: documentId,
          versionId: entry.id,
        })
      }
    />
  );
}
